import { withDevtools } from '@angular-architects/ngrx-toolkit';
import { computed, inject, Signal } from '@angular/core';
import { downloadBlobUrlFileToBrowser } from '@highlander/common/helpers/file.helpers';
import { MmRequest, MmRequestListItem } from '@models/products-and-orders/requests';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { catchError, EMPTY, pipe, switchMap, tap } from 'rxjs';
import { getOne, StateDictionary } from '../../../helpers';
import { MmRequestsService } from './mm-requests.service';

type MmRequestsState = {
  selectedMmRequest: MmRequest | null;
  isSelectedMmRequestLoading: boolean;
  isDownloadLoading: boolean;
};
const initialState: MmRequestsState = {
  selectedMmRequest: null,
  isSelectedMmRequestLoading: false,
  isDownloadLoading: false,
};

const mmRequestsDictionary: StateDictionary<MmRequestListItem> = {
  selectedEntity: 'selectedMmRequest',
  selectedEntityLoading: 'isSelectedMmRequestLoading',
  entityId: 'id',
  // unused
  createLoading: '',
  entityList: '',
  listLoading: '',
};
export const MmRequestsStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withDevtools('mm-requests-store'),

  withComputed(store => ({
    selectedMmRequestId: computed(() => store.selectedMmRequest()?.id || null),
  })),

  withMethods((store, mmRequestsService = inject(MmRequestsService)) => {
    return {
      getOne: getOne<MmRequest>({
        store,
        service: mmRequestsService,
        dictionary: mmRequestsDictionary,
      }),

      downloadFile: rxMethod<{ id: string; fileId: string }>(
        pipe(
          tap(() => patchState(store, { isDownloadLoading: true })),
          switchMap(({ id, fileId }) =>
            mmRequestsService.getDownloadUrl(id, fileId).pipe(
              tap(url => {
                downloadBlobUrlFileToBrowser(url.URL);
                patchState(store, {
                  isDownloadLoading: false,
                });
              }),
              catchError(() => {
                patchState(store, { isDownloadLoading: false });
                return EMPTY;
              }),
            ),
          ),
        ),
      ),

      downloadFileBulk: rxMethod<{ id: string; fileList: string[] }>(
        pipe(
          tap(() => patchState(store, { isDownloadLoading: true })),
          switchMap(({ id, fileList }) =>
            mmRequestsService.getDownloadUrlBulk(id, fileList).pipe(
              tap(url => {
                downloadBlobUrlFileToBrowser(url.URL);
                patchState(store, {
                  isDownloadLoading: false,
                });
              }),
              catchError(() => {
                patchState(store, { isDownloadLoading: false });
                return EMPTY;
              }),
            ),
          ),
        ),
      ),
    };
  }),
);

export interface MmRequestsStoreType {
  // State properties
  readonly selectedMmRequest: Signal<MmRequest | null>;
  readonly isSelectedMmRequestLoading: Signal<boolean>;
  readonly isDownloadLoading: Signal<boolean>;
  readonly selectedMmRequestId: Signal<string | null>;

  // Methods
  readonly getOne: (id: string) => void;
  readonly downloadFile: (params: { id: string; fileId: string }) => void;
  readonly downloadFileBulk: (params: { id: string; fileList: string[] }) => void;
}
